import { isProxy, toRaw } from "vue";
import { buildArticleLinkFromHit } from "~/composables/ExpApi/expArticle";
import type { AisArticle, Article, TestResult, MediumData } from "@expert/types";
import { mapFromHit } from '~/composables/Mapper/Article';

export function mapToExpPrice(rawData: any, storeId: string, article?) {
    const response = {}
    let price = rawData?.[storeId]?.pubData
    if (price === undefined){
        return response;
    }
    return mapExpPrice(price)
}

export function mapExpPrice(price) {
    const response = {};
	if (price) {
		response.nettoPrice = price.price?.net
		response.bruttoPrice = price.price?.gross
		response.taxRate = price.price?.taxRate
		if (price.price?.monthly) {
			response.monthly = true
		}

		response.onlineButtonAction = price.onlineButtonAction;
		if (price.onlineAvailability !== null) {
			response.onlineAvailability = price.onlineAvailability?.color;
			response.minOnlineDelivery = price.onlineAvailability?.minLeadTimeDays;
			response.maxOnlineDelivery = price.onlineAvailability?.maxLeadTimeDays;
		}
		response.shipmentArray = new Array();
		if (price.onlineShipment !== null) {
			for (let a = 0; a < price.onlineShipment?.length; a++) {
				const ob = {
					shipmentType: price.onlineShipment[a].type,
					shipmentNettoPrice: price.onlineShipment[a].price?.net,
					shipmentBruttoPrice: price.onlineShipment[a].price?.gross,
					shipmentTaxRate: price.onlineShipment[a].price?.taxRate,
					shipmentCurrency: price.onlineShipment[a].price?.currency,
					hideVskText: price.onlineShipment[a].hideVskText,
				};
				response.shipmentArray?.push(ob);
			}
		}
		if (price.storeAvailability !== null) {
			response.storeButtonAction = price.storeButtonAction;
			response.storeAvailability = price.storeAvailability?.color;
			response.minStoreDelivery = price.storeAvailability?.minLeadTimeDays;
			response.maxStoreDelivery = price.storeAvailability?.maxLeadTimeDays;
		}
		if (price.basicPrice && price.basicPriceQuantity && price.basicPriceUnit) {
			response.basicPrice = price.basicPrice;
			response.basicPriceQuantity = price.basicPriceQuantity;
			response.basicPriceUnit = price.basicPriceUnit == "KILOGRAM" ? "kg" : (price.basicPriceUnit == "LITER" ? "l" : price.basicPriceUnit);
		}
		response.itemOnDisplay = price.itemOnDisplay;
		response.availableServices = price.availableServices;
		response.createdOn = price.createdOn;
		response.lastUpdated = price.lastUpdated;
		response.pubSource = price.pubSource;
	}
	return response;
}

export function mapToExpBrand(rawData: any) {
	let brand = {
		id: rawData.brand.id,
		logo: rawData.brand.logo,
		name: rawData.brand.name,
	};
	return brand;
}

export function mapToExpMedium(data: any, mediumId: string) {
	let idString = "medium/id=" + mediumId;
	const { data: medium } = useNuxtData(idString);
	let cached = medium?.value;
	if (cached) {
		return cached;
	} else {
	}

	let result: MediumData = {
		id: mediumId,
		originalData: data.originalData,
		mediumType: data.mediumType,
		name: data.name,
		thumbhash: data.thumbhash,
		alt: data?.altText !== undefined ? data.altText : data.name,
		title: data?.title !== undefined ? data.title : data.name,
	};
	if (data.height !== undefined && data.height !== null && Number.isInteger(data.height)) {
		result.height = data.height;
	}
	if (data.width !== undefined && data.width !== null && Number.isInteger(data.width)) {
		result.width = data.width;
	}

	medium.value = result;
	return result;
}

/**
 * @deprecated Use mapper from /components/mapper/Article instead
 */
export function mapToExpArticle(rawData: any, slug: string) {
	// let articleCache = nuxtStorage?.localStorage?.getData('getArticleView-article-' + rawData.articleId);
	const { data: article } = useNuxtData(`article/id=${rawData?.articleId}`);

	let cached = article?.value;
	if (cached) {
		return cached;
		// console.debug("[bt-exp-search] found cached", cached);
	} else {
		// console.debug("found nothing", article);
	}

	// if (articleCache) {
	//     if (articleCache.slug === undefined) {
	//         articleCache.slug = slug;
	//         nuxtStorage?.localStorage?.setData('getArticleView-article-' + articleCache.articleId, articleCache, 1, 'h');
	//         nuxtStorage?.localStorage?.setData('getArticleByExpNr-webcode-' + articleCache.webcode, articleCache, 1, 'h');
	//     }
	//     return articleCache;
	// }

	const response: Article = {};
	if (rawData.contextSensitiveData !== null) {
		response.descriptionList = rawData.contextSensitiveData?.priceTagText;
		response.description = rawData.contextSensitiveData?.description;
		response.headline = rawData.contextSensitiveData?.headline;
		response.formerPriceState = rawData.contextSensitiveData?.formerPriceState;
		response.primaryImage = rawData.contextSensitiveData?.primaryImage;
		response.imageGallery = rawData.contextSensitiveData?.gallery;
		response.active = rawData.contextSensitiveData?.active;
		response.context = rawData.contextSensitiveData?.key;
		response.documentList = rawData.contextSensitiveData?.downloadDocuments;
		if (response.documentList === undefined || response.documentList === null) {
			response.documentList = new Array();
		}
		response.title = rawData.contextSensitiveData?.title;
		response.seoDescription = rawData.contextSensitiveData?.seoTitle;
		response.seoPageTitle = rawData.contextSensitiveData?.pageTitle;
		response.primaryCategory = rawData.contextSensitiveData?.primaryCategory;
		response.crossSaleArticles = rawData.contextSensitiveData?.crossSaleArticles;
		response.additionalArticles = rawData.contextSensitiveData?.additionalArticles;
		response.scopeOfDelivery = rawData.contextSensitiveData?.packageContents;
		response.testResults = new Array();
		for (let b = 0; b < rawData.contextSensitiveData?.testScores.length; b++) {
			let ob: TestResult = {};
			ob.image = rawData.contextSensitiveData?.testScores[b].image;
			ob.link = rawData.contextSensitiveData?.testScores[b].link;
			ob.title = rawData.contextSensitiveData?.testScores[b].title;
			ob.description = rawData.contextSensitiveData?.testScores[b]?.description;
			ob.priority = rawData.contextSensitiveData?.testScores[b].priority;
			response.testResults.push(ob);
		}
	}
	if (rawData.contextSensitiveData?.formerPrice !== "" && rawData.contextSensitiveData?.formerPrice !== null) {
		response.formerNettoPrice = rawData.contextSensitiveData?.formerPrice?.net;
		response.formerBruttoPrice = rawData.contextSensitiveData?.formerPrice?.gross;
		response.formerCurrency = rawData.contextSensitiveData?.formerPrice?.currency;
		response.formerTaxRate = rawData.contextSensitiveData?.formerPrice?.taxRate;
	}
	response.nameSubscript = rawData.contextSensitiveData?.nameSubscript;

	response.articleId = rawData.articleId;
	response.slug = slug;

	if (rawData.articleProperties !== null && rawData.articleProperties !== undefined) {
		response.featureLogos = rawData.articleProperties?.featureLogos;
		response.productType = rawData.articleProperties?.productType;
		response.webcode = rawData.articleProperties?.articleNumber;
		response.internationalArticleNumber = rawData.articleProperties?.internationalArticleNumber;
		response.variationId = rawData.articleProperties?.variationId;
		response.brandId = rawData.articleProperties?.brand;
		response.visibility = rawData.articleProperties?.visibility;
		if (
			rawData.articleProperties.classification?.values["@arrayMap"].length !== 0 &&
			rawData.articleProperties.classification.values["@arrayMap"].length !== null
		) {
			response.technicalData = rawData.articleProperties.classification.values["@arrayMap"];
		}
		if (
			rawData.articleProperties.classification.values["@arrayMap"].length !== 0 &&
			rawData.articleProperties.classification.values["@arrayMap"].length !== null
		) {
			response.technicalData = rawData.articleProperties.classification.values["@arrayMap"];
		}
	}
	response.technicalData?.forEach((feat) => {
		if (feat.key === "59ba44f5a7b11b000152d48a") {
			// console.debug('found feat: ', feat);
			response.documentList.push(feat?.value?.value);
		}
	});

	// if(nuxtStorage?.localStorage?.getData('article-' + response.articleId))
	// let articleResult = nuxtStorage?.localStorage?.getData('getArticleView-article-' + response.articleId);
	// if (!articleResult) {
	//     // console.log("putting result in cache:",response);
	//     nuxtStorage?.localStorage?.setData('getArticleView-article-' + response.articleId, response, 1, 'h');
	//     nuxtStorage?.localStorage?.setData('getArticleByExpNr-webcode-' + response.webcode, response, 1, 'h');

	// }
	article.value = response;
	const { data: webcode } = useNuxtData(`article/webcode=${response.webcode}`);
	webcode.value = response;
	return response;
}

export function mapFromElastic(rawData: any) {
	let rawestData = isProxy(rawData) ? toRaw(rawData) : rawData;
	let slug = buildArticleLinkFromHit(rawestData);
	let article = mapToExpArticle(rawestData?.articleView, slug);
	let medium = mapToExpMedium(rawestData?.primaryImageData, article?.primaryImage);
	let brand = mapToExpBrand(rawestData);
	return { slug: slug, article: article, primaryImage: medium, brand: brand };
}

